
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { generateDrills, generateAIPracticeInsights, PracticeInsight } from '../services/geminiService';
import { Drill, GameEvent, Player, Team, isGameType, AppSettings, DEFAULT_INSIGHT_STATS, MatchRecord } from '../types';
import {
  getPracticePlan, upsertPracticePlan,
  getTeamPracticeTemplates, createTeamPracticeTemplate,
  updateTeamPracticeTemplate, deleteTeamPracticeTemplate,
  clearDefaultPracticeTemplates, getTeamFavoriteDrills, upsertTeamFavoriteDrills,
} from '../services/supabase';
import {
  Sparkles, Loader2, Clock, Target, Plus, GripVertical, Trash2,
  Layout, Save, X, ArrowRight, BarChart2, TrendingUp, AlertCircle,
  ChevronDown, ChevronUp, Search, Calendar, BookOpen, ChevronLeft, ChevronRight,
  CheckCircle2, Settings, Pencil, Bookmark, Upload, Check, RefreshCw
} from 'lucide-react';
import InsightStatSettings, { isMetricOn } from './InsightStatSettings';

interface PracticeBlock {
  id: string;
  title: string;
  drills: Drill[];
}

interface PracticeDrillsProps {
  events: GameEvent[];
  matchRecords?: MatchRecord[];
  players: Player[];
  teams: Team[];
  organizationId?: string;
  teamId?: string;
  currentOrgRole?: string;
  initialDate?: string;
  onEditingChange?: (isDirty: boolean) => void;
  registerLeaveRequest?: (handler: (targetView: number) => void) => void;
  changeView?: (view: number) => void;
  appSettings?: AppSettings;
  setAppSettings?: React.Dispatch<React.SetStateAction<AppSettings>>;
}

interface DrillCardProps {
  drill: Drill;
  practicePlan: PracticeBlock[];
  onAddToBlock: (drill: Drill, blockId: string) => void;
  canEdit?: boolean;
}

interface SectionTemplate {
  id: string;
  name: string;
  drills: Drill[];
}

interface PracticeTemplate {
  id: string;
  name: string;
  blocks: Array<{ title: string; drills: Drill[] }>;
  isDefault?: boolean;
}

const BLOCK_SECTION_COLORS: Record<string, string> = {
  'Warm Up': 'bg-amber-100 text-amber-700',
  'Skill Development': 'bg-blue-100 text-blue-700',
  'Team Play': 'bg-green-100 text-green-700',
};

const DrillCard: React.FC<DrillCardProps> = ({ drill, practicePlan, onAddToBlock, canEdit = true }) => {
  const [expanded, setExpanded] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const [addedBlockTitle, setAddedBlockTitle] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const hasMore = drill.instructions.length > 3 || drill.description.length > 150;

  const suggestedBlock = useMemo(() => {
    if (!drill.suggestedBlock) return null;
    return practicePlan.find(b =>
      b.title.toLowerCase() === drill.suggestedBlock!.toLowerCase()
    ) ?? null;
  }, [drill.suggestedBlock, practicePlan]);

  useEffect(() => {
    if (!showPicker) return;
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPicker]);

  const handleAdd = (blockId: string, blockTitle: string) => {
    onAddToBlock(drill, blockId);
    setAddedBlockTitle(blockTitle);
    setShowPicker(false);
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 1800);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow flex flex-col justify-between">
      <div>
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1 min-w-0 mr-2">
            <h4 className="font-bold text-lg text-slate-900">{drill.name}</h4>
            {drill.suggestedBlock && (
              <span className={`inline-block text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full mt-1 ${BLOCK_SECTION_COLORS[drill.suggestedBlock] ?? 'bg-slate-100 text-slate-500'}`}>
                {drill.suggestedBlock}
              </span>
            )}
          </div>
          <div className="flex items-center text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded shrink-0">
            <Clock className="w-3 h-3 mr-1" />{drill.duration}
          </div>
        </div>
        <p className={`text-slate-600 text-sm mb-4 ${expanded ? '' : 'line-clamp-3'}`}>{drill.description}</p>
        <div className="bg-indigo-50 rounded-lg p-3 mb-3">
          <h5 className="text-xs font-bold text-indigo-900 uppercase tracking-wide mb-1">Instructions</h5>
          <ul className="list-disc list-outside ml-4 space-y-1">
            {(expanded ? drill.instructions : drill.instructions.slice(0, 3)).map((step, i) => (
              <li key={i} className={`text-xs text-indigo-800 leading-relaxed ${expanded ? '' : 'truncate'}`}>{step}</li>
            ))}
          </ul>
        </div>
        {hasMore ? (
          <button
            onClick={() => setExpanded(e => !e)}
            className="text-xs text-indigo-600 font-bold flex items-center gap-1 mb-4 hover:text-indigo-800 transition-colors"
          >
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {expanded ? 'Show less' : 'Show more'}
          </button>
        ) : (
          <div className="mb-1" />
        )}
      </div>

      <div ref={pickerRef} className="relative">
        {justAdded ? (
          <div className="w-full py-2 bg-green-500 text-white rounded-lg text-sm font-bold flex items-center justify-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Added to {addedBlockTitle}!
          </div>
        ) : canEdit ? (
          <button
            onClick={() => setShowPicker(p => !p)}
            className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" /> Add to Practice Plan
          </button>
        ) : null}

        {showPicker && (
          <div className="absolute bottom-full left-0 right-0 mb-1 bg-white rounded-lg shadow-xl border border-slate-200 z-10 p-1">
            <div className="text-[10px] uppercase font-bold text-slate-400 px-2 py-1">Choose a block</div>
            {practicePlan.map(block => (
              <button
                key={block.id}
                onClick={() => handleAdd(block.id, block.title)}
                className={`w-full text-left px-3 py-2 text-sm rounded-md flex items-center justify-between transition-colors ${block.id === suggestedBlock?.id ? 'bg-indigo-50 text-indigo-700 font-bold hover:bg-indigo-100' : 'text-slate-700 hover:bg-slate-50'}`}
              >
                {block.title}
                {block.id === suggestedBlock?.id && <span className="text-[10px] font-bold text-indigo-400 uppercase">suggested</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const DEFAULT_PLAN: PracticeBlock[] = [
  { id: 'block-1', title: 'Warm Up', drills: [] },
  { id: 'block-2', title: 'Skill Development', drills: [] },
  { id: 'block-3', title: 'Team Play', drills: [] },
];

const SKILL_LEVELS = [
  { id: 'youth',       label: 'Youth',        detail: 'U12 & under' },
  { id: 'middle',      label: 'Middle School', detail: 'U14–U15' },
  { id: 'high_school', label: 'High School',   detail: 'U16–U18' },
  { id: 'club',        label: 'Club/College',  detail: 'Competitive' },
  { id: 'adult',       label: 'Adult/Rec',     detail: 'Open league' },
] as const;

type SkillLevelId = typeof SKILL_LEVELS[number]['id'];

const DRILL_CATEGORIES = [
  { id: 'offense',   label: 'Offense',  suggestions: ['Back-row attacks', 'Middle quicks', 'Tooling the block', 'Out-of-system attacking'] },
  { id: 'defense',   label: 'Defense',  suggestions: ['Digging hard swings', 'Serve receive seams', 'Reading the setter', 'Hitter coverage'] },
  { id: 'systems',   label: 'Systems',  suggestions: ['Free ball transition', 'First ball sideout', '6-on-6 wash drills', 'Serve receive under pressure'] },
  { id: 'basics',    label: 'Basics',   suggestions: ['Passing Accuracy', 'Aggressive Serving', 'Setter Transitions', 'Blocking Footwork'] },
] as const;

type DrillCategoryId = typeof DRILL_CATEGORIES[number]['id'] | null;

function getRandomSuggestions(count = 6): string[] {
  const all = DRILL_CATEGORIES.flatMap(c => c.suggestions);
  const shuffled = [...all].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

const localDateStr = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const PracticeDrills: React.FC<PracticeDrillsProps> = ({ events, matchRecords = [], players, teams, organizationId, teamId, currentOrgRole, initialDate, onEditingChange, registerLeaveRequest, changeView, appSettings, setAppSettings }) => {
  const today = localDateStr(new Date());
  const orgKey = organizationId || 'default';
  const favKey = `favoriteDrills_${orgKey}`;
  const sectionTplKey = `sectionTemplates_${orgKey}`;
  const practiceTplKey = `practiceTemplates_${orgKey}`;
  // Coaches (head_coach or assistant) can edit; all other roles are view-only
  const isCoach = currentOrgRole === 'head_coach' || currentOrgRole === 'assistant';
  // Use Supabase whenever a real org is available (not dev mode) — no teamId required
  const useSupabase = !!organizationId && organizationId !== 'dev-org-id';
  const [syncing, setSyncing] = useState(false);

  // --- DATE STATE ---
  const [planDate, setPlanDate] = useState<string>(initialDate || today);
  const storageKey = `practicePlan_${orgKey}_${planDate}`;

  // --- SEARCH-BASED DRILL STATE ---
  const [skillLevel, setSkillLevel] = useState<SkillLevelId>(() => {
    try { return (localStorage.getItem('sideoutiq_practice_skill_level') as SkillLevelId) || 'high_school'; } catch { return 'high_school'; }
  });
  const handleSkillLevelChange = (level: SkillLevelId) => {
    setSkillLevel(level);
    try { localStorage.setItem('sideoutiq_practice_skill_level', level); } catch {}
  };
  const [focusArea, setFocusArea] = useState('');
  const [drills, setDrills] = useState<Drill[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');

  // --- AI INSIGHTS STATE ---
  // PREMIUM FEATURE: Stat-driven AI practice recommendations.
  // This feature will be gated behind a subscription paywall in a future release.
  const [insights, setInsights] = useState<PracticeInsight[]>([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState('');
  const [expandedInsight, setExpandedInsight] = useState<number | null>(null);
  const [showInsightSettings, setShowInsightSettings] = useState(false);

  const insightStats = { ...DEFAULT_INSIGHT_STATS, ...appSettings?.insightStats };

  // Helper: resolve a plan for a given date, falling back to default practice template
  const resolvePlan = useCallback((date: string): PracticeBlock[] => {
    try {
      const saved = localStorage.getItem(`practicePlan_${orgKey}_${date}`);
      if (saved) return JSON.parse(saved) as PracticeBlock[];
    } catch { /* ignore */ }
    try {
      const templates = JSON.parse(localStorage.getItem(practiceTplKey) ?? '[]') as PracticeTemplate[];
      const defaultTpl = templates.find(t => t.isDefault);
      if (defaultTpl) {
        return defaultTpl.blocks.map((b, i) => ({
          id: `block-${Date.now()}-${i}`,
          title: b.title,
          drills: b.drills.map(d => ({ ...d })),
        }));
      }
    } catch { /* ignore */ }
    return DEFAULT_PLAN;
  }, [orgKey, practiceTplKey]);

  // --- PRACTICE PLAN STATE ---
  const [practicePlan, setPracticePlan] = useState<PracticeBlock[]>(() => resolvePlan(initialDate || today));
  // 'saved' = last known good state; used only to keep isDirty always false so the leave guard never blocks
  const [savedPlanJson, setSavedPlanJson] = useState<string>(() => {
    try {
      const key = `practicePlan_${orgKey}_${initialDate || today}`;
      const saved = localStorage.getItem(key);
      return saved ?? JSON.stringify(DEFAULT_PLAN);
    } catch { return JSON.stringify(DEFAULT_PLAN); }
  });
  const isDirty = useMemo(() => JSON.stringify(practicePlan) !== savedPlanJson, [practicePlan, savedPlanJson]);
  const [pendingNavView, setPendingNavView] = useState<number | null>(null);

  // Auto-save status: shown in place of the old Save button
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('saved');
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Prevents spurious auto-save triggers on initial mount and Supabase loads
  const suppressAutoSaveRef = useRef(true);

  // Auto-save: fires on every plan change, debounces Supabase write by 1s
  useEffect(() => {
    if (suppressAutoSaveRef.current) {
      suppressAutoSaveRef.current = false;
      return;
    }
    try { localStorage.setItem(storageKey, JSON.stringify(practicePlan)); } catch { /* quota exceeded */ }
    setSavedPlanJson(JSON.stringify(practicePlan));
    setSaveStatus('saving');
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      setSaveStatus('saved');
      if (useSupabase && organizationId) {
        upsertPracticePlan(organizationId, planDate, practicePlan).catch(() => {});
      }
    }, 800);
  }, [practicePlan]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onEditingChange?.(isDirty);
  }, [isDirty, onEditingChange]);

  useEffect(() => {
    registerLeaveRequest?.((targetView) => {
      setPendingNavView(targetView);
    });
  }, [registerLeaveRequest]);

  const handleDateChange = useCallback((newDate: string) => {
    try { localStorage.setItem(storageKey, JSON.stringify(practicePlan)); } catch { /* ignore */ }
    suppressAutoSaveRef.current = true;
    setPlanDate(newDate);
    try {
      const newKey = `practicePlan_${orgKey}_${newDate}`;
      const saved = localStorage.getItem(newKey);
      if (saved) {
        const newPlan = JSON.parse(saved) as PracticeBlock[];
        setPracticePlan(newPlan);
        setSavedPlanJson(JSON.stringify(newPlan));
        return;
      }
    } catch { /* ignore */ }
    // No saved plan — try default practice template
    try {
      const templates = JSON.parse(localStorage.getItem(practiceTplKey) ?? '[]') as PracticeTemplate[];
      const defaultTpl = templates.find(t => t.isDefault);
      if (defaultTpl) {
        const newPlan: PracticeBlock[] = defaultTpl.blocks.map((b, i) => ({
          id: `block-${Date.now()}-${i}`,
          title: b.title,
          drills: b.drills.map(d => ({ ...d })),
        }));
        setPracticePlan(newPlan);
        setSavedPlanJson(JSON.stringify(newPlan));
        return;
      }
    } catch { /* ignore */ }
    setPracticePlan(DEFAULT_PLAN);
    setSavedPlanJson(JSON.stringify(DEFAULT_PLAN));
  }, [storageKey, practicePlan, orgKey, practiceTplKey]);

  const prevInitialDateRef = useRef(initialDate);
  useEffect(() => {
    if (initialDate && initialDate !== prevInitialDateRef.current) {
      handleDateChange(initialDate);
      prevInitialDateRef.current = initialDate;
    }
  }, [initialDate, handleDateChange]);

  const shiftDate = (days: number) => {
    const d = new Date(planDate + 'T12:00:00');
    d.setDate(d.getDate() + days);
    handleDateChange(localDateStr(d));
  };

  // --- FAVORITES ---
  const [favoriteDrills, setFavoriteDrills] = useState<Drill[]>(() => {
    try {
      const saved = localStorage.getItem(favKey);
      if (saved) return JSON.parse(saved) as Drill[];
    } catch { /* ignore */ }
    return [];
  });

  useEffect(() => {
    try { localStorage.setItem(favKey, JSON.stringify(favoriteDrills)); } catch { /* ignore */ }
  }, [favoriteDrills, favKey]);

  const isFavorite = useCallback((drill: Drill) =>
    favoriteDrills.some(f => f.name.trim().toLowerCase() === drill.name.trim().toLowerCase()),
  [favoriteDrills]);

  const toggleFavorite = useCallback((drill: Drill) => {
    setFavoriteDrills(prev => {
      const next = prev.some(f => f.name.trim().toLowerCase() === drill.name.trim().toLowerCase())
        ? prev.filter(f => f.name.trim().toLowerCase() !== drill.name.trim().toLowerCase())
        : [...prev, drill];
      if (useSupabase && teamId && organizationId) {
        upsertTeamFavoriteDrills(teamId, organizationId, next).catch(() => {});
      }
      return next;
    });
  }, [useSupabase, teamId, organizationId]);

  // --- SECTION TEMPLATES ---
  const [sectionTemplates, setSectionTemplates] = useState<SectionTemplate[]>(() => {
    try {
      const saved = localStorage.getItem(sectionTplKey);
      if (saved) return JSON.parse(saved) as SectionTemplate[];
    } catch { /* ignore */ }
    return [];
  });
  const [savingSectionFor, setSavingSectionFor] = useState<string | null>(null);
  const [newSectionTplName, setNewSectionTplName] = useState('');
  const [loadingSectionFor, setLoadingSectionFor] = useState<string | null>(null);

  useEffect(() => {
    try { localStorage.setItem(sectionTplKey, JSON.stringify(sectionTemplates)); } catch { /* ignore */ }
  }, [sectionTemplates, sectionTplKey]);

  const saveSectionTemplate = async (blockId: string) => {
    const name = newSectionTplName.trim();
    if (!name) return;
    const block = practicePlan.find(b => b.id === blockId);
    if (!block || block.drills.length === 0) return;
    const localId = `stpl-${Date.now()}`;
    setSectionTemplates(prev => [...prev, { id: localId, name, drills: block.drills }]);
    setSavingSectionFor(null);
    setNewSectionTplName('');
    if (useSupabase && teamId && organizationId) {
      const remoteId = await createTeamPracticeTemplate(teamId, organizationId, name, 'section', block.drills);
      if (remoteId) {
        setSectionTemplates(prev => prev.map(t => t.id === localId ? { ...t, id: remoteId } : t));
      }
    }
  };

  const applySectionTemplate = (tpl: SectionTemplate, blockId: string) => {
    setPracticePlan(prev => prev.map(b =>
      b.id === blockId ? { ...b, drills: tpl.drills.map(d => ({ ...d })) } : b
    ));
    setLoadingSectionFor(null);
  };

  const deleteSectionTemplate = (id: string) => {
    setSectionTemplates(prev => prev.filter(t => t.id !== id));
    if (useSupabase) deleteTeamPracticeTemplate(id).catch(() => {});
  };

  // --- PRACTICE TEMPLATES ---
  const [practiceTemplates, setPracticeTemplates] = useState<PracticeTemplate[]>(() => {
    try {
      const saved = localStorage.getItem(practiceTplKey);
      if (saved) return JSON.parse(saved) as PracticeTemplate[];
    } catch { /* ignore */ }
    return [];
  });
  const [showSavePracticeTpl, setShowSavePracticeTpl] = useState(false);
  const [newPracticeTplName, setNewPracticeTplName] = useState('');
  const [showManageTpls, setShowManageTpls] = useState(false);
  const [justAppliedTplId, setJustAppliedTplId] = useState<string | null>(null);

  useEffect(() => {
    try { localStorage.setItem(practiceTplKey, JSON.stringify(practiceTemplates)); } catch { /* ignore */ }
  }, [practiceTemplates, practiceTplKey]);

  const savePracticeTemplate = async () => {
    const name = newPracticeTplName.trim();
    if (!name) return;
    const blocks = practicePlan.map(b => ({ title: b.title, drills: b.drills }));
    const localId = `ptpl-${Date.now()}`;
    setPracticeTemplates(prev => [...prev, { id: localId, name, blocks, isDefault: false }]);
    setShowSavePracticeTpl(false);
    setNewPracticeTplName('');
    if (useSupabase && teamId && organizationId) {
      const remoteId = await createTeamPracticeTemplate(teamId, organizationId, name, 'full', blocks);
      if (remoteId) {
        setPracticeTemplates(prev => prev.map(t => t.id === localId ? { ...t, id: remoteId } : t));
      }
    }
  };

  const applyPracticeTemplate = (tpl: PracticeTemplate) => {
    const newBlocks: PracticeBlock[] = tpl.blocks.map((b, i) => ({
      id: `block-${Date.now()}-${i}`,
      title: b.title,
      drills: b.drills.map(d => ({ ...d })),
    }));
    setPracticePlan(newBlocks);
    setJustAppliedTplId(tpl.id);
  };

  const deletePracticeTemplate = (id: string) => {
    setPracticeTemplates(prev => prev.filter(t => t.id !== id));
    if (useSupabase) deleteTeamPracticeTemplate(id).catch(() => {});
  };

  const overwritePracticeTemplate = async (id: string) => {
    const blocks = practicePlan.map(b => ({ title: b.title, drills: b.drills }));
    setPracticeTemplates(prev => prev.map(t => t.id === id ? { ...t, blocks } : t));
    if (useSupabase) updateTeamPracticeTemplate(id, { data: blocks }).catch(() => {});
  };

  // Returns true when the current practicePlan matches a template's blocks structurally
  const planMatchesTemplate = (tpl: PracticeTemplate): boolean => {
    const nonEmpty = tpl.blocks.filter(b => b.drills.length > 0);
    const planNonEmpty = practicePlan.filter(b => b.drills.length > 0);
    if (nonEmpty.length !== planNonEmpty.length) return false;
    return nonEmpty.every((tb, i) => {
      const pb = planNonEmpty[i];
      if (tb.title !== pb.title) return false;
      if (tb.drills.length !== pb.drills.length) return false;
      return tb.drills.every((d, j) => d.name === pb.drills[j].name);
    });
  };

  const toggleDefaultTemplate = async (id: string) => {
    const tpl = practiceTemplates.find(t => t.id === id);
    if (!tpl) return;
    const newDefault = !tpl.isDefault;
    setPracticeTemplates(prev => prev.map(t => ({ ...t, isDefault: t.id === id ? newDefault : false })));
    if (useSupabase && teamId) {
      if (newDefault) await clearDefaultPracticeTemplates(teamId);
      await updateTeamPracticeTemplate(id, { is_default: newDefault });
    }
  };

  // --- SUPABASE SYNC: load practice plan when org/date changes ---
  useEffect(() => {
    if (!useSupabase || !organizationId) return;
    let cancelled = false;
    setSyncing(true);
    getPracticePlan(organizationId, planDate).then(blocks => {
      if (cancelled || blocks === null) { setSyncing(false); return; }
      const plan = blocks as PracticeBlock[];
      suppressAutoSaveRef.current = true;
      setPracticePlan(plan);
      setSavedPlanJson(JSON.stringify(plan));
      setSaveStatus('saved');
      try { localStorage.setItem(storageKey, JSON.stringify(plan)); } catch { /* ignore */ }
      setSyncing(false);
    }).catch(() => setSyncing(false));
    return () => { cancelled = true; };
  }, [useSupabase, organizationId, planDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- SUPABASE SYNC: load templates + favorites when team changes ---
  useEffect(() => {
    if (!useSupabase || !teamId || !organizationId) return;
    let cancelled = false;
    getTeamPracticeTemplates(teamId).then(rows => {
      if (cancelled) return;
      const full: PracticeTemplate[] = rows
        .filter(r => r.template_type === 'full')
        .map(r => ({ id: r.id, name: r.name, blocks: r.data as Array<{ title: string; drills: Drill[] }>, isDefault: r.is_default }));
      const sections: SectionTemplate[] = rows
        .filter(r => r.template_type === 'section')
        .map(r => ({ id: r.id, name: r.name, drills: r.data as Drill[] }));

      if (full.length > 0) {
        // Supabase is authoritative when it has data
        setPracticeTemplates(full);
      } else {
        // Supabase returned empty — preserve localStorage-seeded state and
        // push any local-only templates up to Supabase so they're persisted
        setPracticeTemplates(prev => {
          if (prev.length > 0 && !cancelled) {
            prev.forEach(t => {
              createTeamPracticeTemplate(teamId, organizationId, t.name, 'full', t.blocks, !!t.isDefault)
                .then(remoteId => {
                  if (remoteId) setPracticeTemplates(p => p.map(x => x.id === t.id ? { ...x, id: remoteId } : x));
                }).catch(() => {});
            });
          }
          return prev;
        });
      }
      if (sections.length > 0) setSectionTemplates(sections);
    }).catch(() => {});
    getTeamFavoriteDrills(teamId).then(drills => {
      if (cancelled || drills.length === 0) return;
      setFavoriteDrills(drills as Drill[]);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [useSupabase, teamId, organizationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- PLAN BLOCKS UI STATE ---
  const [isAddingBlock, setIsAddingBlock] = useState(false);
  const [newBlockTitle, setNewBlockTitle] = useState('');
  const [draggedDrill, setDraggedDrill] = useState<{ blockId: string, drillIndex: number } | null>(null);
  const [draggedBlock, setDraggedBlock] = useState<string | null>(null);
  // Insertion indicators — show a line *between* items rather than highlighting the hovered item
  const [drillInsert, setDrillInsert] = useState<{ blockId: string; beforeIndex: number } | null>(null);
  const [blockInsertBefore, setBlockInsertBefore] = useState<number | null>(null);
  const [isAddingDrill, setIsAddingDrill] = useState<{ blockId: string } | null>(null);
  const [newDrillForm, setNewDrillForm] = useState<Partial<Drill>>({ name: '', duration: '10 min', description: '' });

  // --- EDIT DRILL IN PLAN ---
  const [editingDrill, setEditingDrill] = useState<{ blockId: string; drillIndex: number } | null>(null);
  const [editDrillForm, setEditDrillForm] = useState<Partial<Drill>>({});

  type RotSOEntry = { soAttempts: number; soWon: number; bpAttempts: number; bpWon: number };
  type PassEntry = { hSum: number; hN: number; pSum: number; pN: number; fSum: number; fN: number; tSum: number; tN: number };
  type ServeEntry = { jump: number; float: number; jumpFloat: number; aces: number; errors: number; total: number };

  // --- AGGREGATE TEAM STATS FROM REAL GAME DATA ---
  const teamStats = useMemo((): {
    kills: number; attackAttempts: number; attackErrors: number;
    aces: number; serveAttempts: number; serveErrors: number;
    digs: number; digErrors: number; blockSolo: number; blockAssist: number; assists: number;
    totalSets: number; gamesTracked: number;
    rotSO: Record<string, RotSOEntry>;
    playerPassData: Record<string, PassEntry>;
    playerServeData: Record<string, ServeEntry>;
    playerMap: Map<string, Player>;
  } => {
    let kills = 0, attackAttempts = 0, attackErrors = 0;
    let aces = 0, serveAttempts = 0, serveErrors = 0;
    let digs = 0, digErrors = 0;
    let blockSolo = 0, blockAssist = 0;
    let assists = 0;
    const setKeys = new Set<string>();

    // Rotation sideout / break-point data (keyed by setter-position rotation "1"–"6")
    const rotSO: Record<string, { soAttempts: number; soWon: number; bpAttempts: number; bpWon: number }> = {
      "1": { soAttempts: 0, soWon: 0, bpAttempts: 0, bpWon: 0 },
      "2": { soAttempts: 0, soWon: 0, bpAttempts: 0, bpWon: 0 },
      "3": { soAttempts: 0, soWon: 0, bpAttempts: 0, bpWon: 0 },
      "4": { soAttempts: 0, soWon: 0, bpAttempts: 0, bpWon: 0 },
      "5": { soAttempts: 0, soWon: 0, bpAttempts: 0, bpWon: 0 },
      "6": { soAttempts: 0, soWon: 0, bpAttempts: 0, bpWon: 0 },
    };

    // Per-player pass breakdown by contact type and incoming serve style
    const playerPassData: Record<string, { hSum: number; hN: number; pSum: number; pN: number; fSum: number; fN: number; tSum: number; tN: number }> = {};
    // Per-player serve type breakdown
    const playerServeData: Record<string, { jump: number; float: number; jumpFloat: number; aces: number; errors: number; total: number }> = {};

    const playerMap = new Map<string, Player>(players.map(p => [p.id, p] as [string, Player]));

    for (const event of events || []) {
      const officialRecord = matchRecords.find(r => r.eventId === event.id && r.isRecord);
      const statsSource = officialRecord?.gameState?.stats ?? event.stats ?? [];
      if (statsSource.length === 0) continue;
      for (const r of statsSource) {
        if (r.setNumber != null) setKeys.add(`${event.id}-${r.setNumber}`);
        switch (r.statType) {
          case 'kill':            kills++;    attackAttempts++; break;
          case 'attack_error':    attackErrors++; attackAttempts++; break;
          case 'attack_attempt':  attackAttempts++; break;
          case 'ace':             aces++;     serveAttempts++;  break;
          case 'serve_error':     serveErrors++; serveAttempts++; break;
          case 'serve_attempt':   serveAttempts++; break;
          case 'dig':             digs++;     break;
          case 'dig_error':       digErrors++; break;
          case 'block_solo':      blockSolo++; break;
          case 'block_assist':    blockAssist++; break;
          case 'assist':          assists++;   break;
        }

        // Rotation sideout from break_point stats (authoritative phase-tagged data only)
        const rot = r.rotation;
        if (rot && rotSO[rot] && r.statType === 'break_point') {
          if (r.phase === 'receive') {
            rotSO[rot].soAttempts++;
            if (!r.flags?.includes('receive_failure')) rotSO[rot].soWon++;
          } else {
            rotSO[rot].bpAttempts++;
            rotSO[rot].bpWon++;
          }
        } else if (rot && rotSO[rot] && (r.statType === 'serve_attempt' || r.statType === 'ace' || r.statType === 'serve_error') && r.phase === 'serve') {
          // Fallback: count serve attempts when no break_point stats exist
          rotSO[rot].bpAttempts++;
          if (r.statType === 'ace') rotSO[rot].bpWon++;
        }

        // Pass by contact type and serve style
        if (r.statType === 'reception' || r.statType === 'reception_error') {
          const pid = r.playerId;
          if (!playerPassData[pid]) playerPassData[pid] = { hSum: 0, hN: 0, pSum: 0, pN: 0, fSum: 0, fN: 0, tSum: 0, tN: 0 };
          const rating = r.statType === 'reception' ? (r.passRating ?? 0) : 0;
          if ((r as any).contactType === 'hands') { playerPassData[pid].hSum += rating; playerPassData[pid].hN++; }
          else if ((r as any).contactType === 'platform') { playerPassData[pid].pSum += rating; playerPassData[pid].pN++; }
          if ((r as any).incomingServeStyle === 'float') { playerPassData[pid].fSum += rating; playerPassData[pid].fN++; }
          else if ((r as any).incomingServeStyle === 'topspin') { playerPassData[pid].tSum += rating; playerPassData[pid].tN++; }
        }

        // Serve type by player
        if (r.statType === 'ace' || r.statType === 'serve_error' || r.statType === 'serve_attempt') {
          const pid = r.playerId;
          if (!playerServeData[pid]) playerServeData[pid] = { jump: 0, float: 0, jumpFloat: 0, aces: 0, errors: 0, total: 0 };
          playerServeData[pid].total++;
          if (r.statType === 'ace') playerServeData[pid].aces++;
          if (r.statType === 'serve_error') playerServeData[pid].errors++;
          if (r.serveType === 'jump') playerServeData[pid].jump++;
          else if (r.serveType === 'float') playerServeData[pid].float++;
          else if (r.serveType === 'jump-float') playerServeData[pid].jumpFloat++;
        }
      }
    }

    const gamesTracked = (events || []).filter(e => {
      if (!isGameType(e.type)) return false;
      const rec = matchRecords.find(r => r.eventId === e.id && r.isRecord);
      return (rec?.gameState?.stats?.length ?? 0) > 0 || (e.stats?.length ?? 0) > 0;
    }).length;
    const totalSets = setKeys.size || Math.max(gamesTracked * 3, 1);

    return { kills, attackAttempts, attackErrors, aces, serveAttempts, serveErrors, digs, digErrors, blockSolo, blockAssist, assists, totalSets, gamesTracked, rotSO, playerPassData, playerServeData, playerMap };
  }, [events, matchRecords, players]);

  const hasStats = teamStats.attackAttempts > 0 || teamStats.serveAttempts > 0 || teamStats.digs > 0;

  const buildStatsSummary = (): string => {
    const pct = (n: number, d: number) => d > 0 ? ((n / d) * 100).toFixed(1) + '%' : 'N/A';
    const dec = (n: number, d: number) => d > 0 ? (n / d).toFixed(2) : 'N/A';
    const perSet = (n: number) => teamStats.totalSets > 0 ? (n / teamStats.totalSets).toFixed(1) : 'N/A';
    const avg = (sum: number, n: number) => n > 0 ? (sum / n).toFixed(2) : null;
    const mon = (id: string) => isMetricOn(insightStats, id);

    const lines = [`Games tracked: ${teamStats.gamesTracked}, Sets estimated: ${teamStats.totalSets}`];

    if (insightStats.attacking) {
      const parts: string[] = [];
      if (mon('kill_pct'))        parts.push(`Kill%: ${pct(teamStats.kills, teamStats.attackAttempts)}`);
      if (mon('hit_efficiency'))  parts.push(`Hit%: ${dec(teamStats.kills - teamStats.attackErrors, teamStats.attackAttempts)}`);
      if (mon('total_kills'))     parts.push(`Kills: ${teamStats.kills}`);
      if (mon('attack_errors'))   parts.push(`Errors: ${teamStats.attackErrors}`);
      if (mon('attack_attempts')) parts.push(`Attempts: ${teamStats.attackAttempts}`);
      if (parts.length > 0) lines.push(`Attacking — ${parts.join(', ')}`);
    }

    if (insightStats.serving) {
      const parts: string[] = [];
      if (mon('ace_rate'))         parts.push(`Ace Rate: ${pct(teamStats.aces, teamStats.serveAttempts)}`);
      if (mon('serve_error_rate')) parts.push(`Service Error Rate: ${pct(teamStats.serveErrors, teamStats.serveAttempts)}`);
      if (mon('total_aces'))       parts.push(`Aces: ${teamStats.aces}`);
      if (mon('service_errors'))   parts.push(`Service Errors: ${teamStats.serveErrors}`);
      if (parts.length > 0) lines.push(`Serving — ${parts.join(', ')}`);
    }

    if (insightStats.defense) {
      const parts: string[] = [];
      if (mon('digs_per_set')) parts.push(`Digs/Set: ${perSet(teamStats.digs)}`);
      if (mon('dig_errors'))   parts.push(`Dig Errors: ${teamStats.digErrors}`);
      if (parts.length > 0) lines.push(`Defense — ${parts.join(', ')}`);
    }

    if (insightStats.blocking) {
      const parts: string[] = [];
      if (mon('blocks_per_set')) parts.push(`Blocks/Set: ${perSet(teamStats.blockSolo + teamStats.blockAssist)}`);
      if (mon('block_solos'))    parts.push(`Solos: ${teamStats.blockSolo}`);
      if (mon('block_assists'))  parts.push(`Assists: ${teamStats.blockAssist}`);
      if (parts.length > 0) lines.push(`Blocking — ${parts.join(', ')}`);
    }

    if (insightStats.setting) {
      const parts: string[] = [];
      if (mon('assists_per_set')) parts.push(`Assists/Set: ${perSet(teamStats.assists)}`);
      if (parts.length > 0) lines.push(`Setting — ${parts.join(', ')}`);
    }

    // Rotation sideout data — include only rotations with enough data
    const rotSO = teamStats.rotSO as Record<string, RotSOEntry>;
    const rotLines = (Object.entries(rotSO) as [string, RotSOEntry][])
      .filter(([, d]) => d.soAttempts >= 3 || d.bpAttempts >= 3)
      .map(([rot, d]) => {
        const so = d.soAttempts > 0 ? `SO: ${pct(d.soWon, d.soAttempts)} (${d.soWon}/${d.soAttempts})` : '';
        const bp = d.bpAttempts > 0 ? `BP: ${pct(d.bpWon, d.bpAttempts)} (${d.bpWon}/${d.bpAttempts})` : '';
        return `  Rotation ${rot}: ${[so, bp].filter(Boolean).join(', ')}`;
      });
    if (rotLines.length > 0) lines.push(`Sideout & Break-Point by Rotation:\n${rotLines.join('\n')}`);

    // Serve type breakdown by player (only if serveType data exists)
    const playerServeData = teamStats.playerServeData as Record<string, ServeEntry>;
    const playerMap = teamStats.playerMap as Map<string, Player>;
    const serveTypeLines: string[] = [];
    for (const [pid, sd] of Object.entries(playerServeData) as [string, ServeEntry][]) {
      const hasTypeData = sd.jump + sd.float + sd.jumpFloat > 0;
      if (!hasTypeData || sd.total < 5) continue;
      const player = playerMap.get(pid);
      if (!player) continue;
      const typeParts: string[] = [];
      if (sd.jump > 0) typeParts.push(`Jump: ${sd.jump}`);
      if (sd.float > 0) typeParts.push(`Float: ${sd.float}`);
      if (sd.jumpFloat > 0) typeParts.push(`Jump-Float: ${sd.jumpFloat}`);
      const errRate = pct(sd.errors, sd.total);
      const aceRate = pct(sd.aces, sd.total);
      serveTypeLines.push(`  ${player.name} (${player.position}): ${typeParts.join(', ')}, Ace%: ${aceRate}, Error%: ${errRate}`);
    }
    if (serveTypeLines.length > 0) lines.push(`Serve Type by Player:\n${serveTypeLines.join('\n')}`);

    // Per-player pass breakdown (contact type and serve style) — only players with enough data
    const playerPassData = teamStats.playerPassData as Record<string, PassEntry>;
    const passLines: string[] = [];
    for (const [pid, pd] of Object.entries(playerPassData) as [string, PassEntry][]) {
      const totalPasses = pd.hN + pd.pN;
      if (totalPasses < 5) continue;
      const player = playerMap.get(pid);
      if (!player) continue;
      const parts: string[] = [];
      const hA = avg(pd.hSum, pd.hN);
      const pA = avg(pd.pSum, pd.pN);
      if (hA && pd.hN >= 3) parts.push(`hands avg ${hA} (n=${pd.hN})`);
      if (pA && pd.pN >= 3) parts.push(`platform avg ${pA} (n=${pd.pN})`);
      const fA = avg(pd.fSum, pd.fN);
      const tA = avg(pd.tSum, pd.tN);
      if (fA && pd.fN >= 3) parts.push(`vs float avg ${fA} (n=${pd.fN})`);
      if (tA && pd.tN >= 3) parts.push(`vs topspin avg ${tA} (n=${pd.tN})`);
      if (parts.length > 0) passLines.push(`  ${player.name} (${player.position}): ${parts.join(', ')}`);
    }
    if (passLines.length > 0) lines.push(`Reception Breakdown by Player:\n${passLines.join('\n')}`);

    return lines.join('\n');
  };

  // --- HANDLERS ---

  const handleGenerateInsights = async () => {
    setInsightsLoading(true);
    setInsightsError('');
    setInsights([]);
    setExpandedInsight(null);
    try {
      const results = await generateAIPracticeInsights(buildStatsSummary(), appSettings?.measurementSystem ?? 'imperial', skillLevel);
      if (results.length > 0) {
        setInsights(results);
        setExpandedInsight(0);
      } else {
        setInsightsError('Could not generate insights. Please try again.');
      }
    } catch {
      setInsightsError('An error occurred while connecting to the AI coach.');
    } finally {
      setInsightsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!focusArea.trim()) return;
    setSearchLoading(true);
    setSearchError('');
    try {
      const results = await generateDrills(focusArea, appSettings?.measurementSystem ?? 'imperial', skillLevel);
      if (results.length > 0) {
        setDrills(results);
      } else {
        setSearchError('Could not generate drills. Please try a different topic.');
      }
    } catch {
      setSearchError('An error occurred while connecting to the AI coach.');
    } finally {
      setSearchLoading(false);
    }
  };

  const [activeCategory, setActiveCategory] = useState<DrillCategoryId>(null);
  const [randomSuggestions] = useState<string[]>(() => getRandomSuggestions(6));

  const visibleSuggestions = activeCategory
    ? DRILL_CATEGORIES.find(c => c.id === activeCategory)!.suggestions
    : randomSuggestions;

  // --- PRACTICE PLAN LOGIC ---

  const addBlock = () => {
    if (!newBlockTitle.trim()) return;
    setPracticePlan([...practicePlan, { id: `block-${Date.now()}`, title: newBlockTitle, drills: [] }]);
    setNewBlockTitle('');
    setIsAddingBlock(false);
  };

  const deleteBlock = (blockId: string) => {
    if (window.confirm("Delete this block and all its drills?")) {
      setPracticePlan(prev => prev.filter(b => b.id !== blockId));
    }
  };

  const addManualDrill = () => {
    if (!isAddingDrill || !newDrillForm.name) return;
    const drill: Drill = {
      name: newDrillForm.name,
      duration: newDrillForm.duration || '10 min',
      description: newDrillForm.description || '',
      focus: 'Manual',
      instructions: []
    };
    setPracticePlan(prev => prev.map(b =>
      b.id === isAddingDrill.blockId ? { ...b, drills: [...b.drills, drill] } : b
    ));
    setIsAddingDrill(null);
    setNewDrillForm({ name: '', duration: '10 min', description: '' });
  };

  const addDrillToBlock = (drill: Drill, blockId: string) => {
    setPracticePlan(prev => prev.map(b =>
      b.id === blockId ? { ...b, drills: [...b.drills, drill] } : b
    ));
  };

  const removeDrillFromBlock = (blockId: string, drillIndex: number) => {
    setPracticePlan(prev => prev.map(b =>
      b.id === blockId ? { ...b, drills: b.drills.filter((_, idx) => idx !== drillIndex) } : b
    ));
  };

  const startEditDrill = (blockId: string, drillIndex: number) => {
    const block = practicePlan.find(b => b.id === blockId);
    if (!block) return;
    const drill = block.drills[drillIndex];
    setEditingDrill({ blockId, drillIndex });
    setEditDrillForm({ name: drill.name, duration: drill.duration, description: drill.description });
  };

  const saveEditDrill = () => {
    if (!editingDrill || !editDrillForm.name?.trim()) return;
    setPracticePlan(prev => prev.map(b =>
      b.id === editingDrill.blockId ? {
        ...b,
        drills: b.drills.map((d, idx) =>
          idx === editingDrill.drillIndex ? { ...d, ...editDrillForm } : d
        )
      } : b
    ));
    setEditingDrill(null);
  };

  // --- DRAG AND DROP ---

  const handleDragStart = (e: React.DragEvent, blockId: string, drillIndex: number) => {
    setDraggedDrill({ blockId, drillIndex });
    e.dataTransfer.effectAllowed = 'move';
  };

  const clearDragState = () => {
    setDraggedDrill(null);
    setDraggedBlock(null);
    setDrillInsert(null);
    setBlockInsertBefore(null);
  };

  const handleBlockDragStart = (e: React.DragEvent, blockId: string) => {
    setDraggedBlock(blockId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrillDragOver = (e: React.DragEvent, blockId: string, drillIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedDrill) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const insertBefore = e.clientY < rect.top + rect.height / 2 ? drillIndex : drillIndex + 1;
    setDrillInsert({ blockId, beforeIndex: insertBefore });
    setBlockInsertBefore(null);
  };

  const handleBlockDragOver = (e: React.DragEvent, blockId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedDrill) {
      // Hovering over the block's empty area — insert at end
      const block = practicePlan.find(b => b.id === blockId);
      setDrillInsert({ blockId, beforeIndex: block?.drills.length ?? 0 });
      setBlockInsertBefore(null);
    } else if (draggedBlock) {
      const blockIndex = practicePlan.findIndex(b => b.id === blockId);
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const insertBefore = e.clientY < rect.top + rect.height / 2 ? blockIndex : blockIndex + 1;
      setBlockInsertBefore(insertBefore);
      setDrillInsert(null);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedDrill && drillInsert) {
      const { blockId: sourceBlockId, drillIndex: sourceIndex } = draggedDrill;
      const { blockId: targetBlockId, beforeIndex } = drillInsert;
      const newPlan = [...practicePlan];
      const sourceBlock = newPlan.find(b => b.id === sourceBlockId);
      const targetBlock = newPlan.find(b => b.id === targetBlockId);
      if (sourceBlock && targetBlock) {
        const [movedDrill] = sourceBlock.drills.splice(sourceIndex, 1);
        // When moving within the same block, removing the source shifts indices above it down by 1
        const finalIndex = sourceBlockId === targetBlockId && sourceIndex < beforeIndex
          ? beforeIndex - 1
          : beforeIndex;
        targetBlock.drills.splice(Math.min(finalIndex, targetBlock.drills.length), 0, movedDrill);
        setPracticePlan(newPlan);
      }
    } else if (draggedBlock && blockInsertBefore !== null) {
      const newPlan = [...practicePlan];
      const sourceIndex = newPlan.findIndex(b => b.id === draggedBlock);
      if (sourceIndex !== -1) {
        const [movedBlock] = newPlan.splice(sourceIndex, 1);
        const finalIndex = sourceIndex < blockInsertBefore ? blockInsertBefore - 1 : blockInsertBefore;
        newPlan.splice(Math.min(finalIndex, newPlan.length), 0, movedBlock);
        setPracticePlan(newPlan);
      }
    }

    clearDragState();
  };

  const getTotalDuration = () => {
    let totalMinutes = 0;
    practicePlan.forEach(b => b.drills.forEach(d => {
      const match = d.duration.match(/(\d+)/);
      if (match) totalMinutes += parseInt(match[1]);
    }));
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h > 0 ? h + 'h ' : ''}${m}m`;
  };

  return (
    <div className="space-y-8 pb-20">

      {/* ─── SECTION 1: AI PRACTICE INSIGHTS (PREMIUM FEATURE) ─── */}
      {/* PREMIUM FEATURE: Stat-driven AI analysis and drill recommendations.
          This section will be gated behind a subscription paywall in a future release. */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-violet-600 to-indigo-600 p-6 text-white relative overflow-hidden">
          <Sparkles className="absolute top-0 right-0 w-40 h-40 opacity-10 -mr-8 -mt-8 pointer-events-none" />
          <div className="relative z-10 flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <BarChart2 className="w-5 h-5 shrink-0" />
                <h2 className="text-xl font-bold">AI Practice Insights</h2>
                <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-400 text-amber-900 px-2 py-0.5 rounded-full">Premium</span>
              </div>
              <p className="text-violet-100 text-sm">
                Gemini analyzes your real game stats to identify weak areas and prescribe targeted drills.
              </p>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="text-violet-200 text-xs font-semibold shrink-0">Level:</span>
                {SKILL_LEVELS.map(lvl => (
                  <button key={lvl.id} type="button" onClick={() => handleSkillLevelChange(lvl.id)}
                    title={lvl.detail}
                    className={`text-xs px-3 py-1 rounded-full font-medium transition-colors ${skillLevel === lvl.id ? 'bg-white text-violet-700' : 'bg-white/15 hover:bg-white/25 text-white'}`}>
                    {lvl.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {setAppSettings && (
                <button
                  onClick={() => setShowInsightSettings(true)}
                  title="Configure which stats are considered"
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                >
                  <Settings className="w-4 h-4 text-white" />
                </button>
              )}
              {hasStats && (
                <button
                  onClick={handleGenerateInsights}
                  disabled={insightsLoading}
                  className="bg-white text-violet-700 px-5 py-2.5 rounded-xl font-bold hover:bg-violet-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm text-sm"
                >
                  {insightsLoading ? <Loader2 className="animate-spin w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
                  {insightsLoading ? 'Analyzing...' : insights.length > 0 ? 'Re-analyze' : 'Analyze My Team'}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="p-6">
          {!hasStats ? (
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="bg-violet-50 rounded-xl p-4 shrink-0 hidden md:flex items-center justify-center w-20 h-20">
                <TrendingUp className="w-10 h-10 text-violet-500" />
              </div>
              <div>
                <h3 className="font-bold text-slate-800 mb-2">Unlock data-driven coaching once you track games</h3>
                <p className="text-slate-500 text-sm mb-4">
                  When you record stats in <strong>Game Time</strong>, this section will automatically analyze your team's performance — finding gaps in attacking efficiency, serving accuracy, defensive range, and more — then prescribe drills specifically for those weaknesses.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  {[
                    { icon: '📊', label: 'Attack Efficiency', desc: 'Kill%, Hit%, error rates vs. benchmarks' },
                    { icon: '🏐', label: 'Serving & Receiving', desc: 'Ace rate, service errors, reception quality' },
                    { icon: '🛡️', label: 'Defense & Blocking', desc: 'Digs per set, block rate, error trends' },
                  ].map(item => (
                    <div key={item.label} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <div className="text-xl mb-1">{item.icon}</div>
                      <div className="font-semibold text-slate-700 text-xs uppercase tracking-wide mb-0.5">{item.label}</div>
                      <div className="text-slate-500 text-xs">{item.desc}</div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-4">
                  Start by playing a game in <strong>Game Time</strong> and recording a few stats — even one game provides useful signal.
                </p>
              </div>
            </div>
          ) : insightsLoading ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Loader2 className="w-10 h-10 text-violet-500 animate-spin mb-4" />
              <p className="font-semibold text-slate-700">Analyzing your team's performance…</p>
              <p className="text-sm text-slate-400 mt-1">Gemini is reviewing your stats and building a practice plan</p>
            </div>
          ) : insightsError ? (
            <div className="flex items-center gap-3 bg-red-50 text-red-700 p-4 rounded-lg border border-red-200">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <span className="text-sm">{insightsError}</span>
            </div>
          ) : insights.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-slate-500 text-sm mb-1">
                You have <strong>{teamStats.gamesTracked} game{teamStats.gamesTracked !== 1 ? 's' : ''}</strong> of stats recorded.
              </p>
              <p className="text-slate-400 text-sm">Click <strong>Analyze My Team</strong> above to get personalized drill recommendations.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-slate-400 mb-4">
                Based on {teamStats.gamesTracked} game{teamStats.gamesTracked !== 1 ? 's' : ''} · {teamStats.attackAttempts} attacks · {teamStats.serveAttempts} serves
              </p>
              {insights.map((insight, i) => (
                <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors text-left"
                    onClick={() => setExpandedInsight(expandedInsight === i ? null : i)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center font-bold text-sm shrink-0">
                        {i + 1}
                      </div>
                      <div>
                        <div className="font-bold text-slate-800">{insight.skill}</div>
                        <div className="text-xs text-slate-500 line-clamp-1">{insight.rationale}</div>
                      </div>
                    </div>
                    {expandedInsight === i
                      ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" />
                      : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                    }
                  </button>
                  {expandedInsight === i && (
                    <div className="border-t border-slate-100 p-4 bg-slate-50">
                      <p className="text-sm text-slate-600 mb-4">{insight.rationale}</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {insight.drills.map((drill, di) => (
                          <DrillCard key={di} drill={drill} practicePlan={practicePlan} onAddToBlock={addDrillToBlock} canEdit={isCoach} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ─── SECTION 2: SEARCH DRILLS BY SKILL ─── */}
      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-2xl p-8 text-white relative overflow-hidden shadow-lg">
        <Sparkles className="absolute top-0 right-0 w-48 h-48 opacity-10 -mr-10 -mt-10 pointer-events-none" />
        <div className="relative z-10 max-w-xl">
          <div className="flex items-center gap-2 mb-1">
            <Search className="w-5 h-5" />
            <h2 className="text-xl font-bold">Search Drills by Skill</h2>
          </div>
          <p className="text-indigo-100 mb-4 text-sm">Tell the AI coach what you want to work on and it'll generate targeted drills to add to your plan.</p>

          <div className="mb-5 flex items-center gap-2 flex-wrap">
            <span className="text-indigo-200 text-xs font-semibold shrink-0">Level:</span>
            {SKILL_LEVELS.map(lvl => (
              <button key={lvl.id} type="button" onClick={() => handleSkillLevelChange(lvl.id)}
                title={lvl.detail}
                className={`text-xs px-3 py-1 rounded-full font-medium transition-colors ${skillLevel === lvl.id ? 'bg-white text-indigo-700' : 'bg-white/15 hover:bg-white/25 text-white'}`}>
                {lvl.label}
              </button>
            ))}
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              value={focusArea}
              onChange={(e) => setFocusArea(e.target.value)}
              placeholder="E.g., Serve Receive, Blocking, Transition"
              className="flex-1 px-4 py-3 rounded-xl text-slate-900 focus:ring-4 focus:ring-indigo-400/30 outline-none"
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button
              onClick={handleSearch}
              disabled={searchLoading || !focusArea}
              className="bg-white text-indigo-600 px-6 py-3 rounded-xl font-bold hover:bg-indigo-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm"
            >
              {searchLoading ? <Loader2 className="animate-spin w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
              <span>Generate</span>
            </button>
          </div>

          <div className="mt-4 flex gap-1.5 flex-wrap">
            <button
              onClick={() => setActiveCategory(null)}
              className={`text-xs font-bold px-3 py-1 rounded-full transition-colors ${activeCategory === null ? 'bg-white text-indigo-700' : 'bg-white/20 hover:bg-white/30 text-white'}`}
            >
              All
            </button>
            {DRILL_CATEGORIES.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(activeCategory === cat.id ? null : cat.id)}
                className={`text-xs font-bold px-3 py-1 rounded-full transition-colors ${activeCategory === cat.id ? 'bg-white text-indigo-700' : 'bg-white/20 hover:bg-white/30 text-white'}`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="mt-2 flex flex-wrap gap-2">
            {visibleSuggestions.map(s => (
              <button
                key={s}
                onClick={() => setFocusArea(s)}
                className={`text-xs px-3 py-1 rounded-full transition-colors border ${focusArea === s ? 'bg-white text-indigo-700 border-white font-bold' : 'bg-white/10 hover:bg-white/25 text-white border-white/30'}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {searchError && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg border border-red-200">{searchError}</div>
      )}

      {drills.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-bold text-xl text-slate-800 flex items-center gap-2">
            <Target className="w-5 h-5 text-indigo-600" />
            Recommended Drills for "{focusArea}"
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {drills.map((drill, idx) => <DrillCard key={idx} drill={drill} practicePlan={practicePlan} onAddToBlock={addDrillToBlock} canEdit={isCoach} />)}
          </div>
        </div>
      )}

      {/* ─── SECTION 3: PRACTICE PLAN ─── */}
      <div className="border-t border-slate-200 pt-8">
        {/* ── Header row ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-3">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Layout className="w-6 h-6 text-slate-700" /> Practice Plan
              {syncing && <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" title="Syncing with team…" />}
            </h3>
            <p className="text-slate-500 text-sm flex items-center gap-2 flex-wrap">
              {useSupabase ? 'Shared across all devices · ' : ''}
              {isCoach ? 'Drag and drop blocks and drills to organize your session.' : <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">View only</span>}
            </p>
          </div>
          {/* Date navigator */}
          <div className="flex items-center gap-2">
            <button onClick={() => shiftDate(-1)} className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500 transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-3 py-1.5">
              <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <input
                type="date"
                value={planDate}
                onChange={e => e.target.value && handleDateChange(e.target.value)}
                className="text-sm font-bold text-slate-700 outline-none bg-transparent cursor-pointer"
              />
            </div>
            <button onClick={() => shiftDate(1)} className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500 transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-slate-100 px-4 py-2 rounded-lg font-bold text-slate-700 text-sm flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-500" />
              Total: {getTotalDuration()}
            </div>
            <div className={`flex items-center gap-1.5 text-sm font-medium transition-all duration-300 ${saveStatus === 'saving' ? 'text-slate-400' : 'text-green-600'}`}>
              {saveStatus === 'saving'
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                : <><CheckCircle2 className="w-4 h-4" /> Saved</>
              }
            </div>
          </div>
        </div>

        {/* ── Practice template controls ── */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="text-xs font-bold text-slate-400 uppercase tracking-wide mr-1">Templates:</span>

          {showSavePracticeTpl ? (
            <div className="flex items-center gap-2">
              <input
                autoFocus
                className="p-1.5 text-sm border border-indigo-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-400 bg-indigo-50 w-44"
                placeholder="Template name"
                value={newPracticeTplName}
                onChange={e => setNewPracticeTplName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') savePracticeTemplate(); if (e.key === 'Escape') setShowSavePracticeTpl(false); }}
              />
              <button onClick={savePracticeTemplate} disabled={!newPracticeTplName.trim()} className="bg-indigo-500 text-white px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-indigo-600 disabled:opacity-40 flex items-center gap-1">
                <Check className="w-3 h-3" /> Save
              </button>
              <button onClick={() => setShowSavePracticeTpl(false)} className="text-slate-400 hover:text-slate-600 p-1">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => { setShowSavePracticeTpl(true); setNewPracticeTplName(''); }}
              className="flex items-center gap-1.5 text-xs font-bold text-slate-600 bg-white border border-slate-200 hover:border-indigo-300 hover:text-indigo-600 px-3 py-1.5 rounded-lg transition-colors"
            >
              <Save className="w-3.5 h-3.5" /> Save as template
            </button>
          )}

          {practiceTemplates.length > 0 && !showSavePracticeTpl && (
            <select
              defaultValue=""
              onChange={e => {
                const id = e.target.value;
                if (!id) return;
                const tpl = practiceTemplates.find(t => t.id === id);
                if (tpl) applyPracticeTemplate(tpl);
                e.target.value = '';
              }}
              className="text-xs font-bold text-slate-600 bg-white border border-slate-200 hover:border-indigo-300 px-3 py-1.5 rounded-lg transition-colors outline-none cursor-pointer"
            >
              <option value="" disabled>Load template…</option>
              {practiceTemplates.map(tpl => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.isDefault ? '★ ' : ''}{tpl.name} ({tpl.blocks.filter(b => b.drills.length > 0).length} block{tpl.blocks.filter(b => b.drills.length > 0).length !== 1 ? 's' : ''})
                </option>
              ))}
            </select>
          )}

          {practiceTemplates.length > 0 && !showSavePracticeTpl && (
            <button
              type="button"
              onClick={() => setShowManageTpls(m => !m)}
              className={`text-xs font-bold px-3 py-1.5 rounded-lg border transition-colors ${showManageTpls ? 'bg-slate-100 text-slate-700 border-slate-300' : 'text-slate-400 border-slate-200 hover:text-slate-600 hover:border-slate-300'}`}
            >
              Manage
            </button>
          )}
        </div>

        {/* ── Post-apply banner ── */}
        {justAppliedTplId && (() => {
          const tpl = practiceTemplates.find(t => t.id === justAppliedTplId);
          if (!tpl) return null;
          const matches = planMatchesTemplate(tpl);
          if (!matches) {
            return (
              <div className="mb-4 flex items-center gap-3 bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3 text-sm">
                <BookOpen className="w-4 h-4 text-indigo-500 shrink-0" />
                <span className="flex-1 text-slate-700"><strong>{tpl.name}</strong> applied.</span>
                <button
                  type="button"
                  onClick={async () => { await overwritePracticeTemplate(tpl.id); setJustAppliedTplId(null); }}
                  className="text-xs font-bold bg-white text-slate-600 border border-slate-200 px-3 py-1.5 rounded-lg hover:bg-slate-50 transition-colors shrink-0"
                >
                  Update "{tpl.name}"
                </button>
                <button type="button" onClick={() => setJustAppliedTplId(null)} className="text-slate-400 hover:text-slate-600 shrink-0">
                  <X className="w-4 h-4" />
                </button>
              </div>
            );
          }
          return (
            <div className="mb-4 flex items-center gap-3 bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3 text-sm">
              <BookOpen className="w-4 h-4 text-indigo-500 shrink-0" />
              <span className="flex-1 text-slate-700">
                <strong>{tpl.name}</strong> applied.
                {!tpl.isDefault && ' Make it the default for future practice days?'}
              </span>
              {tpl.isDefault ? (
                <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-1 rounded-lg">★ Already default</span>
              ) : (
                <button
                  type="button"
                  onClick={() => { toggleDefaultTemplate(justAppliedTplId); setJustAppliedTplId(null); }}
                  className="text-xs font-bold bg-indigo-600 text-white px-3 py-1.5 rounded-lg hover:bg-indigo-700 transition-colors shrink-0"
                >
                  Set as default
                </button>
              )}
              <button type="button" onClick={() => setJustAppliedTplId(null)} className="text-slate-400 hover:text-slate-600 shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>
          );
        })()}

        {/* ── Manage templates panel ── */}
        {showManageTpls && practiceTemplates.length > 0 && (
          <div className="mb-6 bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
              <h4 className="text-sm font-bold text-slate-700">Manage Templates</h4>
              <button onClick={() => setShowManageTpls(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-3 space-y-2">
              {practiceTemplates.map(tpl => (
                <div key={tpl.id} className="flex items-center justify-between gap-2 px-3 py-2.5 border border-slate-100 rounded-lg hover:bg-slate-50">
                  <div className="min-w-0 flex-1">
                    <span className="font-bold text-sm text-slate-800">{tpl.name}</span>
                    <span className="text-xs text-slate-400 ml-2">{tpl.blocks.filter(b => b.drills.length > 0).length} block{tpl.blocks.filter(b => b.drills.length > 0).length !== 1 ? 's' : ''}</span>
                    {tpl.isDefault && (
                      <span className="ml-2 text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-bold">★ default</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => toggleDefaultTemplate(tpl.id)}
                      title={tpl.isDefault ? 'Remove as default' : 'Auto-apply to new practice days'}
                      className={`text-xs px-2 py-1 rounded-lg border font-bold transition-colors ${tpl.isDefault ? 'bg-green-100 text-green-700 border-green-200 hover:bg-green-200' : 'text-slate-400 border-slate-200 hover:text-green-600 hover:border-green-300'}`}
                    >
                      {tpl.isDefault ? '★ Default' : 'Set default'}
                    </button>
                    <button
                      type="button"
                      onClick={() => deletePracticeTemplate(tpl.id)}
                      className="text-slate-300 hover:text-red-500 p-1 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
              <p className="text-xs text-slate-400 px-1 pt-1">The default template auto-applies when opening a practice day with no existing plan.</p>
            </div>
          </div>
        )}

        {/* ── Plan blocks ── */}
        <div className="flex flex-col gap-2">
          {practicePlan.map((block, blockIndex) => (
            <React.Fragment key={block.id}>
              {/* Block insertion line — shown above this block when dragging */}
              {blockInsertBefore === blockIndex && (
                <div className="h-1 rounded-full bg-blue-500 mx-2 transition-all" />
              )}
            <div
              className={`bg-slate-50 rounded-xl border-2 transition-all duration-200 ${draggedBlock === block.id ? 'opacity-40 border-dashed border-slate-400' : 'border-slate-200'}`}
              draggable={isCoach}
              onDragStart={isCoach ? (e) => handleBlockDragStart(e, block.id) : undefined}
              onDragEnd={isCoach ? clearDragState : undefined}
              onDragOver={isCoach ? (e) => handleBlockDragOver(e, block.id) : undefined}
              onDrop={isCoach ? handleDrop : undefined}
            >
              {/* Block header */}
              <div className="p-4 border-b border-slate-200 flex flex-col gap-2 bg-white rounded-t-xl">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    {isCoach && (
                      <div className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500">
                        <GripVertical className="w-5 h-5" />
                      </div>
                    )}
                    <h4 className="font-bold text-lg text-slate-800">{block.title}</h4>
                    <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full font-bold">
                      {block.drills.length} Drills
                    </span>
                  </div>
                  {isCoach && (
                    <div className="flex items-center gap-1">
                      {/* Save section as template */}
                      <button
                        type="button"
                        onClick={() => {
                          setSavingSectionFor(savingSectionFor === block.id ? null : block.id);
                          setLoadingSectionFor(null);
                          setNewSectionTplName(block.title);
                        }}
                        title="Save these drills as a reusable section"
                        className={`p-2 rounded-lg transition-colors ${savingSectionFor === block.id ? 'text-indigo-600 bg-indigo-50' : 'text-slate-400 hover:text-indigo-500 hover:bg-slate-50'}`}
                      >
                        <Save className="w-4 h-4" />
                      </button>
                      {/* Load section template */}
                      {sectionTemplates.length > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            setLoadingSectionFor(loadingSectionFor === block.id ? null : block.id);
                            setSavingSectionFor(null);
                          }}
                          title="Load a saved section"
                          className={`p-2 rounded-lg transition-colors ${loadingSectionFor === block.id ? 'text-indigo-600 bg-indigo-50' : 'text-slate-400 hover:text-indigo-500 hover:bg-slate-50'}`}
                        >
                          <Upload className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setIsAddingDrill({ blockId: block.id })}
                        className="text-blue-600 hover:bg-blue-50 p-2 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors"
                      >
                        <Plus className="w-4 h-4" /> Add Drill
                      </button>
                      <button type="button" onClick={() => deleteBlock(block.id)} className="text-slate-400 hover:text-red-500 p-2 rounded-lg transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Save section template inline form */}
                {savingSectionFor === block.id && (
                  <div className="flex gap-2 items-center pl-8">
                    <input
                      autoFocus
                      className="flex-1 p-1.5 text-sm border border-indigo-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-400 bg-indigo-50"
                      placeholder={`Section name (e.g. ${block.title})`}
                      value={newSectionTplName}
                      onChange={e => setNewSectionTplName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') saveSectionTemplate(block.id); if (e.key === 'Escape') setSavingSectionFor(null); }}
                    />
                    <button
                      type="button"
                      onClick={() => saveSectionTemplate(block.id)}
                      disabled={!newSectionTplName.trim() || block.drills.length === 0}
                      className="bg-indigo-500 text-white px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-indigo-600 disabled:opacity-40 flex items-center gap-1"
                    >
                      <Check className="w-3 h-3" /> Save
                    </button>
                    <button type="button" onClick={() => setSavingSectionFor(null)} className="text-slate-400 hover:text-slate-600 p-1">
                      <X className="w-3.5 h-3.5" />
                    </button>
                    {block.drills.length === 0 && (
                      <span className="text-xs text-amber-500">Add drills first</span>
                    )}
                  </div>
                )}

                {/* Load section template panel */}
                {loadingSectionFor === block.id && sectionTemplates.length > 0 && (
                  <div className="pl-8 space-y-1.5">
                    <p className="text-xs text-slate-400 font-medium">Replace block drills with saved section:</p>
                    {sectionTemplates.map(tpl => (
                      <div key={tpl.id} className="flex items-center justify-between gap-2 px-3 py-2 bg-indigo-50 border border-indigo-100 rounded-lg">
                        <div className="min-w-0">
                          <span className="text-sm font-bold text-slate-800">{tpl.name}</span>
                          <span className="text-xs text-slate-400 ml-2">{tpl.drills.length} drill{tpl.drills.length !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <button
                            type="button"
                            onClick={() => applySectionTemplate(tpl, block.id)}
                            className="bg-indigo-500 text-white text-xs font-bold px-2.5 py-1 rounded-lg hover:bg-indigo-600 transition-colors"
                          >
                            Apply
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteSectionTemplate(tpl.id)}
                            className="text-slate-300 hover:text-red-500 p-1 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                    <button type="button" onClick={() => setLoadingSectionFor(null)} className="text-xs text-slate-400 hover:text-slate-600">Cancel</button>
                  </div>
                )}
              </div>

              {/* Drill list */}
              <div className="p-4 space-y-2 min-h-[100px]">
                {block.drills.length === 0 && (
                  <div className="border-2 border-dashed border-slate-200 rounded-lg overflow-hidden">
                    <div className="text-center py-5 text-slate-400 text-sm">
                      Drag AI drills here or add manually
                    </div>
                    {favoriteDrills.length > 0 && (
                      <div className="border-t border-slate-100 px-4 pb-4 pt-3">
                        <p className="text-xs text-slate-400 font-medium mb-2 flex items-center gap-1">
                          <Bookmark className="w-3 h-3 fill-amber-400 text-amber-400" /> Favorites
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {favoriteDrills.map((fav, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => addDrillToBlock(fav, block.id)}
                              className="text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium hover:bg-amber-100 transition-colors flex items-center gap-1"
                            >
                              <Plus className="w-3 h-3" />{fav.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {block.drills.map((drill, drillIndex) => {
                  const isEditing = editingDrill?.blockId === block.id && editingDrill?.drillIndex === drillIndex;
                  const starred = isFavorite(drill);
                  const isDraggingThis = draggedDrill?.blockId === block.id && draggedDrill?.drillIndex === drillIndex;
                  const showInsertBefore = drillInsert?.blockId === block.id && drillInsert.beforeIndex === drillIndex;
                  if (isEditing) {
                    return (
                      <div key={`${block.id}-${drillIndex}-edit`} className="bg-indigo-50 p-3 rounded-lg border border-indigo-200 space-y-2">
                        <div className="flex gap-2">
                          <input
                            autoFocus
                            className="flex-1 p-2 text-sm border border-indigo-200 rounded bg-white outline-none focus:ring-2 focus:ring-indigo-400 font-bold"
                            placeholder="Drill Name"
                            value={editDrillForm.name ?? ''}
                            onChange={e => setEditDrillForm(f => ({ ...f, name: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter') saveEditDrill(); if (e.key === 'Escape') setEditingDrill(null); }}
                          />
                          <input
                            className="w-24 p-2 text-sm border border-indigo-200 rounded bg-white outline-none focus:ring-2 focus:ring-indigo-400"
                            placeholder="10 min"
                            value={editDrillForm.duration ?? ''}
                            onChange={e => setEditDrillForm(f => ({ ...f, duration: e.target.value }))}
                          />
                        </div>
                        <input
                          className="w-full p-2 text-sm border border-indigo-200 rounded bg-white outline-none focus:ring-2 focus:ring-indigo-400"
                          placeholder="Brief description (optional)"
                          value={editDrillForm.description ?? ''}
                          onChange={e => setEditDrillForm(f => ({ ...f, description: e.target.value }))}
                        />
                        <div className="flex justify-end gap-2">
                          <button onClick={() => setEditingDrill(null)} className="text-xs text-slate-500 hover:text-slate-700 px-3 py-1">Cancel</button>
                          <button onClick={saveEditDrill} className="bg-indigo-600 text-white text-xs font-bold px-3 py-1 rounded hover:bg-indigo-700">Save</button>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <React.Fragment key={`${block.id}-${drillIndex}`}>
                      {showInsertBefore && (
                        <div className="h-0.5 rounded-full bg-blue-500 mx-1" />
                      )}
                    <div
                      draggable={isCoach}
                      onDragStart={isCoach ? (e) => handleDragStart(e, block.id, drillIndex) : undefined}
                      onDragEnd={isCoach ? clearDragState : undefined}
                      onDragOver={isCoach ? (e) => handleDrillDragOver(e, block.id, drillIndex) : undefined}
                      onDrop={isCoach ? handleDrop : undefined}
                      className={`bg-white p-3 rounded-lg border shadow-sm flex items-start justify-between group hover:border-blue-300 transition-all
                        ${isCoach ? 'cursor-grab active:cursor-grabbing' : ''}
                        ${isDraggingThis ? 'opacity-40 bg-slate-100 border-slate-200' : 'border-slate-200'}
                      `}
                    >
                      <div className="flex items-start gap-3 flex-1 min-w-0 mr-3">
                        {isCoach && <GripVertical className="w-4 h-4 text-slate-300 shrink-0 mt-0.5" />}
                        <div className="min-w-0">
                          <div className="font-bold text-slate-900 break-words">{drill.name}</div>
                          <div className="text-xs text-slate-500 break-words">{drill.description || drill.focus || 'Drill'}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="flex items-center text-xs font-bold text-slate-500 bg-slate-50 px-2 py-1 rounded">
                          <Clock className="w-3 h-3 mr-1" /> {drill.duration}
                        </div>
                        {/* Star / favorite — always visible, amber when starred */}
                        <button
                          type="button"
                          onClick={e => { e.stopPropagation(); toggleFavorite(drill); }}
                          title={starred ? 'Remove from favorites' : 'Save as favorite'}
                          className={`transition-colors ${starred ? 'text-amber-400 hover:text-amber-500' : 'text-slate-300 hover:text-amber-400'}`}
                        >
                          <Bookmark className="w-5 h-5" fill={starred ? 'currentColor' : 'none'} />
                        </button>
                        {isCoach && <>
                          <button
                            type="button"
                            onClick={e => { e.stopPropagation(); startEditDrill(block.id, drillIndex); }}
                            className="text-indigo-400 hover:text-indigo-600 transition-colors"
                          >
                            <Pencil className="w-5 h-5" />
                          </button>
                          <button
                            type="button"
                            onClick={e => { e.stopPropagation(); removeDrillFromBlock(block.id, drillIndex); }}
                            className="text-red-400 hover:text-red-600 transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </>}
                      </div>
                    </div>
                    </React.Fragment>
                  );
                })}
                {/* Insertion line at the end of the drill list */}
                {drillInsert?.blockId === block.id && drillInsert.beforeIndex === block.drills.length && (
                  <div className="h-0.5 rounded-full bg-blue-500 mx-1" />
                )}

                {/* Add Drill form */}
                {isAddingDrill?.blockId === block.id && (
                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 space-y-2">
                    <div className="flex gap-2">
                      <input
                        autoFocus
                        className="flex-1 p-2 text-sm border border-blue-200 rounded bg-white outline-none focus:ring-2 focus:ring-blue-400"
                        placeholder="Drill Name"
                        value={newDrillForm.name}
                        onChange={(e) => setNewDrillForm({ ...newDrillForm, name: e.target.value })}
                        onKeyDown={e => e.key === 'Enter' && addManualDrill()}
                      />
                      <input
                        className="w-24 p-2 text-sm border border-blue-200 rounded bg-white outline-none focus:ring-2 focus:ring-blue-400"
                        placeholder="10 min"
                        value={newDrillForm.duration}
                        onChange={(e) => setNewDrillForm({ ...newDrillForm, duration: e.target.value })}
                      />
                    </div>
                    <input
                      className="w-full p-2 text-sm border border-blue-200 rounded bg-white outline-none focus:ring-2 focus:ring-blue-400"
                      placeholder="Brief description (optional)"
                      value={newDrillForm.description}
                      onChange={(e) => setNewDrillForm({ ...newDrillForm, description: e.target.value })}
                    />
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setIsAddingDrill(null)} className="text-xs text-slate-500 hover:text-slate-700 px-3 py-1">Cancel</button>
                      <button onClick={addManualDrill} className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded hover:bg-blue-700">Add Drill</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </React.Fragment>
          ))}
          {/* Insertion line at the very end of the block list */}
          {blockInsertBefore === practicePlan.length && (
            <div className="h-1 rounded-full bg-blue-500 mx-2 transition-all" />
          )}

          {isCoach && (isAddingBlock ? (
            <div className="bg-white p-4 rounded-xl border-2 border-blue-200 border-dashed flex gap-2 items-center">
              <input
                autoFocus
                className="flex-1 p-2 text-sm border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Block Title (e.g. Conditioning)"
                value={newBlockTitle}
                onChange={e => setNewBlockTitle(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addBlock()}
              />
              <button onClick={addBlock} className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700"><ArrowRight className="w-4 h-4" /></button>
              <button onClick={() => setIsAddingBlock(false)} className="text-slate-400 hover:text-slate-600 p-2"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <button
              onClick={() => setIsAddingBlock(true)}
              className="w-full py-4 border-2 border-dashed border-slate-300 rounded-xl text-slate-500 font-bold hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" /> Add Practice Block
            </button>
          ))}
        </div>
      </div>

      {/* ── Leave Without Saving Confirmation Modal ── */}
      {pendingNavView !== null && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="p-5 border-b border-slate-200">
              <h3 className="text-base font-bold text-slate-900">Unsaved Changes</h3>
              <p className="text-sm text-slate-500 mt-1">
                Your practice plan has unsaved changes. What would you like to do?
              </p>
            </div>
            <div className="flex flex-col gap-2 p-4">
              <button
                onClick={() => {
                  const target = pendingNavView;
                  setPendingNavView(null);
                  changeView?.(target);
                }}
                className="w-full py-2.5 rounded-lg bg-green-600 text-white font-bold text-sm hover:bg-green-700 transition-colors"
              >
                Leave
              </button>
              <button
                onClick={() => setPendingNavView(null)}
                className="w-full py-2.5 rounded-lg text-slate-500 font-medium text-sm hover:bg-slate-100 transition-colors"
              >
                Stay & Keep Editing
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── AI INSIGHT STAT SETTINGS MODAL ─── */}
      {setAppSettings && (
        <InsightStatSettings
          isOpen={showInsightSettings}
          onClose={() => setShowInsightSettings(false)}
          appSettings={appSettings ?? { substitutionsPerSet: 12, measurementSystem: 'imperial' }}
          setAppSettings={setAppSettings}
        />
      )}
    </div>
  );
};

export default PracticeDrills;
